import React from "react";
import isEmpty from "lodash/isEmpty";
import { useRouter } from "next/router";
import {
  Loader as CardsPlaceHolder,
  Button,
  BUTTON_VARIANTS,
  BUTTON_SIZES,
  Image,
} from "@gonoodle/gn-universe-ui";
import { concatenateQueryParams } from "@gonoodle/gn-universe-utils";
import { get } from "lodash";
import {
  SECTIONS,
  SECTIONS_TYPES,
} from "@gonoodle/gn-universe-analytics-schema/src/constants";
import {
  HeartIcon as HeartIconOutline,
  ClockIcon as ClockIconOutline,
} from "@heroicons/react/outline";
import { twMerge } from "tailwind-merge";

import { useDataLoader } from "../../hooks";
import VideoTile from "../../../VideoTile";
import PrintableTile from "../../../PrintableTile";
import TextTile from "../../../TextTile";
import CustomLinkCardTile from "../../../CustomLinkCardTile";
import BlogCardTile from "../../../BlogCardTile";
import {
  CONTENT_TYPES,
  ROUTE_PREFIX,
  ROUTE_PATHS,
  PROFILE_TYPES,
} from "../../../../constants";
import { getSourcePageAndSourcePageTypeFromReferrer } from "../../../../utils/analytics";
import { useUser } from "../../../../contexts/user";
import { useCurrentChampion, useProfile } from "../../../../hooks";

const JSURL = require("jsurl");

const PLACEHOLDER_TYPES = {
  PROMPT_ADD_TO_FAVORITES: "promptAddToFavorites",
  PROMPT_START_WATCHING: "promptStartWatching",
  PROMPT_SIGNUP_OR_LOGIN_TO_TRACK_HISTORY: "promptSignUpOrLogInToTrackHistory",
  PROMPT_SIGNUP_OR_LOGIN_TO_SAVE_FAVORITES:
    "promptSignUpOrLogInToSaveFavorites",
};

export default function CardList({ templateOptions, fetchers }) {
  const {
    title,
    displayTitle,
    description,
    size,
    placeholder = {},
  } = templateOptions;
  const [fetcher] = fetchers;
  const { data, isLoading } = useDataLoader(fetcher);
  const { contentType, content: cards = [] } = get(data, "[0]", {});

  /**
   * Conditionally render the ProfileStreaks component:
   * Due to a backend limitation, when the `size` prop is exactly 3,
   * it indicates that we need to add the Profile Streaks to a shelf.
   * This specific condition is used to implicitly determine the display
   * of Profile Streaks based on the provided size.
   */
  const includesProfileStreaks = size === 3;

  if (isLoading) {
    return (
      <div className="grid gap-x-md gap-y-md md:grid-cols-2 lg:grid-cols-4">
        <CardsPlaceHolder cols={4} size="short" mode="dark" />
      </div>
    );
  }

  // Placeholder types are used to determine the type of list to render
  if (
    [
      PLACEHOLDER_TYPES.PROMPT_ADD_TO_FAVORITES,
      PLACEHOLDER_TYPES.PROMPT_SIGNUP_OR_LOGIN_TO_SAVE_FAVORITES,
    ].includes(placeholder.type)
  ) {
    return (
      <div
        className={twMerge(
          "flex flex-col",
          includesProfileStreaks && "lg:flex-row-reverse",
        )}
      >
        {includesProfileStreaks ? <ProfileStreaks /> : null}

        <FavoritesList
          title={displayTitle || title}
          description={description}
          size={size}
          cards={cards}
        />
      </div>
    );
  }

  // Placeholder types are used to determine the type of list to render
  if (
    [
      PLACEHOLDER_TYPES.PROMPT_START_WATCHING,
      PLACEHOLDER_TYPES.PROMPT_SIGNUP_OR_LOGIN_TO_TRACK_HISTORY,
    ].includes(placeholder.type)
  ) {
    return (
      <div
        className={twMerge(
          "flex flex-col",
          includesProfileStreaks && "lg:flex-row-reverse",
        )}
      >
        {includesProfileStreaks ? <ProfileStreaks /> : null}

        <RecentlyWatchedList
          title={displayTitle || title}
          description={description}
          size={size}
          cards={cards}
        />
      </div>
    );
  }

  return (
    <div
      className={twMerge(
        "flex flex-col",
        includesProfileStreaks && "lg:flex-row-reverse",
      )}
    >
      {includesProfileStreaks ? <ProfileStreaks /> : null}

      <RandomList
        title={displayTitle || title}
        description={description}
        contentType={contentType} // content type is used to support old templates
        size={size}
        cards={cards}
      />
    </div>
  );
}

function ListHeader({ title, description }) {
  return (
    <span>
      <h2 className="text-lg font-bold tracking-tight text-white capitalize">
        {title}
      </h2>

      <p className="gn-text-sm text-white">{description}</p>
    </span>
  );
}

function FavoritesList({ title, description, size = 4, cards }) {
  const router = useRouter();
  const { user } = useUser();
  const limitedCards = size ? cards.slice(0, size) : cards; // Limit the number of cards based on the size prop

  if (!user.isLoggedIn && limitedCards.length === 0) {
    return (
      <div className="flex flex-col w-full">
        <div className="flex flex-row items-center mb-6">
          <ListHeader title={title} description={description} />
        </div>

        <div className="bg-gray-800 w-full flex flex-col justify-center items-center space-y-10 py-16 px-1 text-center">
          <HeartIconOutline
            className="w-32 h-32 text-gray-100"
            aria-hidden="true"
          />

          <span className="text-lg text-gray-100">
            Create a Free Account to start favoriting videos and see them here!
          </span>

          <Button
            variant={BUTTON_VARIANTS.light}
            size={BUTTON_SIZES.sm}
            className="font-regular lg:whitespace-nowrap"
            onPress={() =>
              router.push(
                concatenateQueryParams(ROUTE_PATHS.REGISTRATION, {
                  referrer: JSURL.stringify({
                    pathname: router.pathname,
                    query: router.query,
                    sourceElement: SECTIONS_TYPES.FAVORITES_SHELF,
                    sourceName: SECTIONS.CREATE_ACCOUNT_BUTTON,
                  }),
                }),
                ROUTE_PATHS.REGISTRATION,
              )
            }
          >
            Create a Free Account
          </Button>
        </div>
      </div>
    );
  }

  if (limitedCards.length === 0) {
    return (
      <div className="flex flex-col w-full">
        <div className="flex flex-row justify-between items-center mb-6">
          <ListHeader title={title} description={description} />

          <ViewFavoritesButton />
        </div>

        <div className="bg-gray-800 w-full flex flex-col justify-center items-center space-y-10 py-16 px-1 text-center">
          <HeartIconOutline
            className="w-32 h-32 text-gray-100"
            aria-hidden="true"
          />

          <span className="text-lg text-gray-100">
            You have no favorited videos
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <div className="flex flex-row justify-between items-center mb-6">
        <ListHeader title={title} description={description} />

        <ViewFavoritesButton />
      </div>

      <ul
        className={twMerge(
          "grid gap-x-md gap-y-md md:grid-cols-2 lg:grid-cols-4",
          size === 3 && "lg:grid-cols-3",
        )}
      >
        {limitedCards.map((card, index) => (
          <VideoListItem
            key={`${card.id}_${index}`}
            card={card.content || card}
            title={title}
          />
        ))}

        {limitedCards.length < size &&
          Array.from({ length: size - limitedCards.length }).map((_, index) => (
            <li key={index} className="flex flex-col justify-top">
              <div className="w-full bg-gray-700 transition-shadow shadow-darkSM aspect-w-16 aspect-h-9">
                <div className="flex flex-col justify-center items-center p-1">
                  <HeartIconOutline
                    className="w-28 h-28 text-gray-100"
                    aria-hidden="true"
                  />

                  <span className="gn-text-sm mt-xs text-gray-100 capitalize">
                    favorite more videos
                  </span>
                </div>
              </div>
            </li>
          ))}
      </ul>
    </div>
  );
}

function RecentlyWatchedList({ title, description, size = 4, cards }) {
  const router = useRouter();
  const { user } = useUser();
  const limitedCards = size ? cards.slice(0, size) : cards; // Limit the number of cards based on the size prop

  if (!user.isLoggedIn && limitedCards.length === 0) {
    return (
      <div className="flex flex-col w-full">
        <div className="flex flex-row items-center mb-6">
          <ListHeader title={title} description={description} />
        </div>

        <div className="bg-gray-800 w-full flex flex-col justify-center items-center space-y-10 py-16 px-1 text-center">
          <ClockIconOutline
            className="w-32 h-32 text-gray-100"
            aria-hidden="true"
          />

          <span className="text-lg text-gray-100">
            Create a free account to start seeing your recently watched videos
            here!
          </span>

          <Button
            variant={BUTTON_VARIANTS.light}
            size={BUTTON_SIZES.sm}
            className="font-regular lg:whitespace-nowrap"
            onPress={() =>
              router.push(
                concatenateQueryParams(ROUTE_PATHS.REGISTRATION, {
                  referrer: JSURL.stringify({
                    pathname: router.pathname,
                    query: router.query,
                    sourceElement: SECTIONS_TYPES.RECENTLY_WATCHED_SHELF,
                    sourceName: SECTIONS.CREATE_ACCOUNT_BUTTON,
                  }),
                }),
                ROUTE_PATHS.REGISTRATION,
              )
            }
          >
            Create a Free Account
          </Button>
        </div>
      </div>
    );
  }

  if (limitedCards.length === 0) {
    return (
      <div className="flex flex-col w-full">
        <div className="flex flex-row items-center mb-6">
          <ListHeader title={title} description={description} />
        </div>

        <div className="bg-gray-800 w-full flex flex-col justify-center items-center space-y-10 py-16 px-1 text-center">
          <ClockIconOutline
            className="w-32 h-32 text-gray-100"
            aria-hidden="true"
          />

          <span className="text-lg text-gray-100">Watch some videos</span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <div className="flex flex-row justify-between items-center mb-6">
        <ListHeader title={title} description={description} />
      </div>

      <ul
        className={twMerge(
          "grid gap-x-md gap-y-md md:grid-cols-2 lg:grid-cols-4",
          size === 3 && "lg:grid-cols-3",
        )}
      >
        {limitedCards.map((card, index) => (
          <VideoListItem
            key={`${card.id}_${index}`}
            card={card.content || card}
            title={title}
          />
        ))}

        {limitedCards.length < size &&
          Array.from({ length: size - limitedCards.length }).map((_, index) => (
            <li key={index} className="flex flex-col justify-top">
              <div className="w-full bg-gray-700 transition-shadow shadow-darkSM aspect-w-16 aspect-h-9">
                <div className="flex flex-col justify-center items-center p-1">
                  <ClockIconOutline
                    className="w-28 h-28 text-gray-100"
                    aria-hidden="true"
                  />

                  <span className="gn-text-sm mt-xs text-gray-100 capitalize">
                    Watch More Videos
                  </span>
                </div>
              </div>
            </li>
          ))}
      </ul>
    </div>
  );
}

function RandomList({ title, description, contentType, size, cards }) {
  const limitedCards = size ? cards.slice(0, size) : cards; // Limit the number of cards based on the size prop

  return (
    <div className="flex flex-col w-full">
      <div className="flex flex-row items-center mb-6">
        <ListHeader title={title} description={description} />
      </div>

      <ul
        className={twMerge(
          "grid gap-x-md gap-y-md grid-cols-1 md:grid-cols-2 lg:grid-cols-4",
          limitedCards.length === 3 && "lg:grid-cols-3",
          limitedCards.length === 5 &&
            "grid-cols-2 lg:grid-cols-3 xl:grid-cols-5",
        )}
      >
        {limitedCards.map((card, index) =>
          contentType === CONTENT_TYPES.TAGS ||
          [
            CONTENT_TYPES.TOPIC,
            CONTENT_TYPES.CHANNEL,
            CONTENT_TYPES.TAG,
          ].includes(card.contentType) ? (
            <TextListItem
              key={`${card.id}_${index}`}
              card={card.content ? card.content : card}
              format={card.content ? "landscape" : "square"}
              title={title}
            />
          ) : contentType === CONTENT_TYPES.VIDEOS ||
            card.contentType === CONTENT_TYPES.VIDEO ? (
            <VideoListItem
              key={`${card.id}_${index}`}
              card={card.content || card}
              title={title}
            />
          ) : contentType === CONTENT_TYPES.ACTIVITIES ||
            card.contentType === CONTENT_TYPES.ACTIVITY ? (
            <PrintableListItem
              key={`${card.id}_${index}`}
              card={card.content || card}
              format={card.content ? "landscape" : "portrait"}
              title={title}
            />
          ) : card.contentType === CONTENT_TYPES.CUSTOM_LINK ? (
            <LinkCardListItem
              key={`${card.id}_${index}`}
              card={card.content}
              title={title}
            />
          ) : card.contentType === CONTENT_TYPES.BLOG_POST ? (
            <BlogPostListItem
              key={`${card.id}_${index}`}
              card={card.content}
              title={title}
            />
          ) : null,
        )}
      </ul>
    </div>
  );
}

function ViewFavoritesButton() {
  const router = useRouter();
  return (
    <Button
      size={BUTTON_SIZES.sm}
      variant={BUTTON_VARIANTS.lightOutline}
      className="capitalize space-x-2 whitespace-nowrap"
      onPress={() => router.push(`/${ROUTE_PREFIX.FAVORITES}`)}
    >
      <HeartIconOutline className="h-5" />
      <span>view favorites</span>
    </Button>
  );
}

const getTile = (card, format) => {
  // card.image is used when Card is in a Custom Shelf AND is not a Tag Card
  if (card.image) return card.image;

  // this is used when card is a Tag Card in a Custom Shelf
  if (format === "landscape") {
    if (card.images?.spotlightImage) return card.images.spotlightImage;
    if (card.images?.tileImage) return card.images.tileImage;
  }

  // this is used when card is a content Tag Card in a content Tag Shelf
  if (card.images?.tileImage) return card.images.tileImage;
  if (card.images?.spotlightImage) return card.images.spotlightImage;

  return null;
};

function TextListItem({ card, format, title }) {
  const router = useRouter();
  const tile = getTile(card, format);

  return (
    <li className="flex flex-col justify-top relative">
      <TextTile
        id={card.id}
        slug={card.slug}
        name={card.name}
        title={card.name}
        description={card.description}
        tile={tile}
        format={format}
        linkParams={{
          sourceElement: SECTIONS_TYPES.SHELF,
          sourceName: title,
          ...getSourcePageAndSourcePageTypeFromReferrer(
            router.pathname,
            router.query,
          ),
        }}
      />
    </li>
  );
}

function VideoListItem({ card, title }) {
  const router = useRouter();
  return (
    <li className="flex flex-col justify-top relative group">
      <VideoTile
        id={card.id}
        slug={card.slug}
        title={card.title}
        description={card.description}
        images={card.images}
        duration={card.duration}
        sponsor={{
          expiration: card.attribution?.expiration,
          type: card.attribution?.type,
        }}
        impressionsProps={
          card?.partner?.partnerId
            ? {
                partnerName: card.partner.partnerName,
                partnerId: card.partner.partnerId,
                sourceElement: SECTIONS_TYPES.SHELF,
                sourceName: title,
                ...getSourcePageAndSourcePageTypeFromReferrer(
                  router.pathname,
                  router.query,
                ),
              }
            : null
        }
        linkParams={{
          sourceElement: SECTIONS_TYPES.SHELF,
          sourceName: title,
          ...getSourcePageAndSourcePageTypeFromReferrer(
            router.pathname,
            router.query,
          ),
        }}
      />
    </li>
  );
}

function PrintableListItem({ card, title, format }) {
  const router = useRouter();
  const sources =
    format === "landscape" && !isEmpty(card.heroImage)
      ? {
          "regular@1x": card.heroImage["thumb@1x"],
          "regular@2x": card.heroImage["thumb@2x"],
          "regular@3x": card.heroImage["thumb@3x"],
        }
      : card.image;
  return (
    <li className="flex flex-col justify-top relative group">
      <PrintableTile
        id={card.id}
        slug={card.slug}
        mode="dark"
        sources={sources}
        title={card.title}
        description={card.description}
        format={format}
        linkParams={{
          sourceElement: SECTIONS_TYPES.SHELF,
          sourceName: title,
          ...getSourcePageAndSourcePageTypeFromReferrer(
            router.pathname,
            router.query,
          ),
        }}
      />
    </li>
  );
}

function LinkCardListItem({ card, title }) {
  const router = useRouter();
  const {
    id,
    slug,
    name,
    description,
    image,
    images,
    title: customLinkTitle,
    destinationUrl,
  } = card;
  return (
    <li className="flex flex-col justify-top relative group">
      <CustomLinkCardTile
        id={id}
        slug={slug}
        sources={images?.spotlightImage || image}
        title={name}
        name={name}
        customLinkTitle={customLinkTitle}
        destinationUrl={destinationUrl}
        description={description}
        format="landscape"
        linkParams={{
          sourceElement: SECTIONS_TYPES.SHELF,
          sourceName: title,
          ...getSourcePageAndSourcePageTypeFromReferrer(
            router.pathname,
            router.query,
          ),
        }}
      />
    </li>
  );
}

function BlogPostListItem({ card, title }) {
  const router = useRouter();
  const { slug, title: blogTitle, description, images } = card;

  return (
    <li className="flex flex-col justify-top relative group">
      <BlogCardTile
        slug={slug}
        title={title}
        name={blogTitle}
        description={description}
        imageUrl={images.tile}
        analyticsProps={{
          sourceElement: SECTIONS_TYPES.SHELF,
          sourceName: title,
          ...getSourcePageAndSourcePageTypeFromReferrer(
            router.pathname,
            router.query,
          ),
        }}
      />
    </li>
  );
}

function ProfileStreaks() {
  const champion = useCurrentChampion();
  const { profile } = useProfile();

  if (!profile) return null;

  return (
    <div className="flex flex-col my-2 md:mt-8 md:mb-0 lg:mt-0 lg:flex-row-reverse lg:items-center">
      <div className="flex flex-row flex-nowrap lg:flex-col">
        <div className="flex flex-1 justify-start lg:basis-auto lg:grow-0 lg:shrink-0">
          <div className="flex flex-col items-center space-y-1 md:flex-row md:space-x-4 md:space-y-0 lg:flex-col lg:space-x-0 lg:space-y-3 lg:w-full lg:justify-center">
            <div className="w-[45px] md:w-[65px] lg:w-[110px] flex-shrink-0">
              <Image
                className="w-full h-full object-contain"
                sources={{
                  "regular@1x": champion.images.full["1x"],
                  "regular@2x": champion.images.full["2x"],
                  "regular@3x": champion.images.full["3x"],
                }}
                alt=""
              />
            </div>

            <span
              className="text-xs text-white line-clamp-2 md:text-sm lg:text-md lg:text-center lg:line-clamp-1 px-1"
              style={{ fontStyle: "italic" }}
            >
              {champion.name}
            </span>
          </div>
        </div>

        <span className="hidden md:block bg-white min-h-full w-[1px] opacity-30 mx-1 lg:hidden" />

        <div className="flex flex-1 justify-center lg:basis-auto lg:grow-0 lg:shrink-0">
          <div
            className="flex flex-col items-center justify-around px-2 py-1 space-y-1 rounded md:flex-row md:rounded-lg md:px-3 md:space-y-0 lg:p-3 lg:items-start lg:mt-[18px]"
            style={{
              background:
                "linear-gradient(94deg, #F0C30F 51.36%, #F4A934 100%)",
            }}
          >
            <div className="flex flex-row items-center space-x-2 md:space-x-3 lg:hidden">
              <span className="w-[22px] h-[22px] md:w-[32px] md:h-[32px] flex justify-center items-center rounded-full bg-white">
                <ThunderIcon />
              </span>

              <span className="font-display text-[24px] leading-[14px] text-gray-900 font-extrabold">
                {profile.classroomType === PROFILE_TYPES.CLASSROOM
                  ? profile.weekStreak
                  : profile.dayStreak}
              </span>
            </div>

            <div className="text-gray-900 text-xs leading-[18px] font-semibold font-display md:ml-3 md:text-md md:leading-6 lg:hidden">
              {profile.classroomType === PROFILE_TYPES.CLASSROOM
                ? "week streak"
                : "day streak"}
            </div>

            <div className="hidden flex-1 lg:block">
              <span className="w-[35px] h-[35px] flex justify-center items-center rounded-full bg-white">
                <ThunderIcon />
              </span>
            </div>

            <div className="hidden flex-1 ml-16 lg:block">
              <div className="flex flex-col items-end">
                <div className="font-display text-[36px] leading-[36px] text-gray-900 font-extrabold">
                  {profile.classroomType === PROFILE_TYPES.CLASSROOM
                    ? profile.weekStreak
                    : profile.dayStreak}
                </div>

                <div className="text-gray-900 text-md whitespace-nowrap font-semibold font-display">
                  {profile.classroomType === PROFILE_TYPES.CLASSROOM
                    ? "week streak"
                    : "day streak"}
                </div>
              </div>
            </div>
          </div>
        </div>

        <span className="hidden md:block bg-white min-h-full w-[1px] opacity-30 mx-1 lg:hidden" />

        <div className="hidden lg:block w-full h-[1px] bg-white opacity-30 my-[18px]" />

        <div className="flex flex-1 justify-end lg:basis-auto lg:grow-0 lg:shrink-0">
          <div className="flex flex-col justify-between space-y-1 md:flex-row md:space-y-0 md:space-x-2 md:items-center lg:flex-row-reverse lg:items-start lg:space-x-0 lg:w-full">
            <span
              className="text-md font-extrabold text-center text-transparent md:text-[18px]"
              style={{
                background:
                  "linear-gradient(94deg, #F0C30F 51.36%, #F4A934 100%)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
              }}
            >
              {profile.classroomType === PROFILE_TYPES.CLASSROOM
                ? profile.schoolYearWeekStreak
                : profile.weekStreak}
            </span>

            <span className="text-white text-xs font-medium text-center md:text-sm lg:max-w-2/3 lg:text-start">
              {profile.classroomType === PROFILE_TYPES.CLASSROOM
                ? "Week Streak in School Year"
                : "Week Streak"}
            </span>
          </div>
        </div>
      </div>

      <div className="hidden md:block bg-gray-500 w-full h-[1px] my-6 lg:my-0 lg:h-full lg:w-[1px] lg:mr-12 lg:ml-14" />
    </div>
  );
}

function ThunderIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
    >
      <path
        d="M6.41305 7.64258C6.97686 7.64258 7.5426 7.64258 8.10641 7.64258C8.11413 7.64258 8.12379 7.64258 8.13151 7.64258C8.16434 7.64258 8.16627 7.64468 8.15082 7.67626C8.13151 7.72046 8.11027 7.76466 8.09096 7.80676C8.02338 7.95411 7.9558 8.10146 7.88822 8.25091C7.8805 8.26775 7.87278 8.28248 7.85154 8.27827C7.83416 8.27406 7.82837 8.2909 7.82064 8.30353C7.78782 8.36457 7.75499 8.42772 7.72217 8.48877C7.64107 8.64243 7.56191 8.79398 7.48081 8.94764C7.41709 9.06973 7.35338 9.19182 7.28966 9.31601C7.21822 9.45073 7.14871 9.58755 7.07726 9.72227C7.01161 9.84856 6.94597 9.97275 6.88032 10.0991C6.81081 10.2317 6.74129 10.3622 6.67178 10.4948C6.59841 10.6337 6.52697 10.7726 6.4536 10.9137C6.37636 11.061 6.29913 11.2084 6.22189 11.3536C6.15624 11.4778 6.09253 11.602 6.02688 11.7262C5.97281 11.8293 5.91875 11.9346 5.86469 12.0377C5.85889 12.0482 5.84924 12.0567 5.8531 12.0714C5.84731 12.0714 5.84538 12.0777 5.84345 12.0819C5.83379 12.0945 5.82414 12.1072 5.81448 12.1177C5.24681 12.7365 4.67914 13.3554 4.11147 13.9764C4.10567 13.9827 4.09795 13.9911 4.0883 13.9995C4.08444 13.9806 4.09409 13.9679 4.09795 13.9553C4.36055 13.2375 4.62508 12.5197 4.88767 11.802C5.28543 10.7158 5.68512 9.62965 6.08287 8.54349C6.18907 8.25091 6.2972 7.96043 6.40339 7.66784C6.40919 7.66152 6.41112 7.651 6.41305 7.64258Z"
        fill="#18181B"
      />
      <path
        d="M6.41107 0.00390625C6.42266 0.00390625 6.43425 0.00390625 6.44583 0.00390625C7.78585 0.00390625 9.12586 0.00390625 10.4639 0.00390625C10.4755 0.00390625 10.4871 0.00390625 10.5045 0.00390625C9.33632 1.70049 8.17202 3.39497 7.00192 5.09367C8.75514 5.09367 10.5006 5.09367 12.25 5.09367C12.2481 5.11051 12.2365 5.11682 12.2287 5.12524C11.3328 6.10194 10.4388 7.07863 9.54292 8.05533C8.32262 9.38565 7.10232 10.716 5.88202 12.0484C5.8743 12.0568 5.8685 12.0674 5.85692 12.0737C5.85306 12.0589 5.86271 12.0505 5.8685 12.04C5.92257 11.9368 5.97663 11.8337 6.0307 11.7285C6.09635 11.6043 6.16006 11.4801 6.22571 11.3559C6.30295 11.2085 6.38018 11.0612 6.45742 10.9159C6.53079 10.777 6.60223 10.6381 6.6756 10.4971C6.74511 10.3645 6.81462 10.2339 6.88414 10.1013C6.94978 9.97714 7.01543 9.85085 7.08108 9.72455C7.15252 9.58983 7.22204 9.45301 7.29348 9.31829C7.3572 9.19621 7.42091 9.07412 7.48463 8.94993C7.5638 8.79627 7.64489 8.64471 7.72599 8.49105C7.75881 8.4279 7.79164 8.36686 7.82446 8.30582C7.83026 8.29319 7.83798 8.27845 7.85536 8.28056C7.8766 8.28477 7.88432 8.27003 7.89204 8.25319C7.95962 8.10585 8.0272 7.9585 8.09478 7.80905C8.11409 7.76484 8.13533 7.72064 8.15464 7.67854C8.16816 7.64697 8.16622 7.64486 8.13533 7.64486C8.12761 7.64486 8.11795 7.64486 8.11023 7.64486C7.54642 7.64486 6.98068 7.64486 6.41687 7.64486C6.40335 7.64486 6.38984 7.64486 6.37632 7.64486C5.43213 7.64486 4.48794 7.64486 3.54375 7.64486C3.53024 7.64486 3.51672 7.64486 3.5032 7.64276C3.5341 7.56066 3.56499 7.47647 3.59782 7.39437C3.65574 7.24492 3.71367 7.09547 3.77159 6.94602C3.83917 6.7671 3.90675 6.58607 3.97626 6.40715C4.04771 6.21981 4.12108 6.03247 4.19252 5.84513C4.26782 5.64937 4.33927 5.45361 4.41457 5.25996C4.4918 5.05578 4.57097 4.8537 4.65013 4.64952C4.71964 4.4706 4.78529 4.28958 4.8548 4.11066C4.93783 3.89385 5.02086 3.67704 5.10388 3.46023C5.18884 3.23921 5.27187 3.02029 5.35683 2.79928C5.46109 2.52563 5.56536 2.25409 5.66963 1.98045C5.77003 1.71523 5.87237 1.45211 5.97277 1.18689C6.04614 0.993231 6.12145 0.799576 6.19482 0.605921C6.25661 0.441735 6.32032 0.279654 6.38211 0.115468C6.3879 0.0754744 6.40914 0.0439002 6.41107 0.00390625Z"
        fill="#F0C30F"
      />
      <path
        d="M6.41109 0.00420989C6.40916 0.0442039 6.38792 0.077883 6.37633 0.111562C6.31455 0.275748 6.25276 0.439934 6.18904 0.602015C6.11567 0.79567 6.04036 0.989325 5.96699 1.18298C5.86659 1.4482 5.76425 1.71132 5.66385 1.97654C5.55958 2.25019 5.45532 2.52173 5.35105 2.79537C5.26609 3.01639 5.18306 3.2353 5.09811 3.45632C5.01508 3.67313 4.93205 3.88994 4.84903 4.10675C4.78145 4.28567 4.71387 4.4667 4.64435 4.64562C4.56712 4.8498 4.48796 5.05187 4.40879 5.25605C4.33349 5.45181 4.26205 5.64757 4.18674 5.84123C4.1153 6.02857 4.04193 6.21591 3.97049 6.40325C3.90291 6.58217 3.83533 6.76319 3.76582 6.94211C3.70789 7.09157 3.64996 7.24102 3.59204 7.39047C3.56114 7.47256 3.53025 7.55676 3.49743 7.63885C3.48777 7.63885 3.47812 7.64096 3.47039 7.64096C2.90658 7.64096 2.34277 7.64096 1.77896 7.64096C1.77124 7.64096 1.76159 7.64096 1.75386 7.63885C1.75193 7.63675 1.75 7.63675 1.75 7.63464C1.78089 7.5336 1.82337 7.43888 1.86006 7.33995C2.13038 6.63058 2.4007 5.92332 2.67102 5.21395C2.93168 4.53406 3.19042 3.85416 3.45109 3.17426C3.78319 2.30702 4.1153 1.43768 4.44548 0.570441C4.51499 0.389415 4.5845 0.206285 4.65401 0.0252594C4.66173 0.00631484 4.66946 0 4.68876 0C5.25837 0 5.82797 0 6.3995 0C6.40337 0.00210495 6.40723 0.00420989 6.41109 0.00420989Z"
        fill="#18181B"
      />
      <path
        d="M5.84534 12.0858C5.84534 12.0789 5.84766 12.0742 5.85696 12.0742C5.85464 12.0789 5.85231 12.0835 5.84534 12.0858Z"
        fill="#F0C30F"
      />
      <path
        d="M1.75 7.63867C1.75195 7.64064 1.75391 7.64064 1.75391 7.6426C1.75 7.6426 1.75 7.6426 1.75 7.63867Z"
        fill="#F0C30F"
      />
    </svg>
  );
}
